import React from "react";
import { Tag } from "antd";
import { MedicineBoxOutlined, RightOutlined } from "@ant-design/icons";
import { formatSearchDate } from "../search/searchResultUtils";

const CHECKIN_TYPE_MAP = { 1: "In-Patient", 2: "Out-Patient" };
const CHECKIN_SOURCE_MAP = { 0: "Facility", 1: "Manual PA" };
const STATUS_MAP = { 0: "Active", 1: "Closed" };

const TYPE_COLOR_MAP = { 1: "magenta", 2: "geekblue" };
const SOURCE_COLOR_MAP = { 1: "gold", 0: "cyan" };
const STATUS_COLOR_MAP = { 0: "green", 1: "default" };

function Fact({ children }) {
  if (children == null || children === "") return null;
  return <span className="listing-card__fact">{children}</span>;
}

function formatPatientName(record) {
  if (record?.consumer) {
    const c = record.consumer;
    return [c.first_name, c.surname].filter(Boolean).join(" ") || "—";
  }
  const full = `${record?.surname?.toUpperCase?.() || ""}${record?.surname ? "," : ""} ${record?.first_name || ""} ${record?.middle_name || ""}`.trim();
  return full || record?.patient_name || "—";
}

function normalizeEncounter(record) {
  if (!record) return {};
  if (record.consumer) {
    return {
      insurance_no: record.consumer?.insurance_no,
      code: record.checkin_id,
      checkin_date: record.checkin_date,
      facility_name: record.facility_name,
      plan_name: record.plan_name,
      statusLabel: record.aggregated_status || "Pending",
      statusColor: "blue",
      isSearch: true,
      filedCount: record?.filed?.line_items_count,
      approvedCount: record?.approved?.approved_line_items_count,
    };
  }

  const statusKey = Number(record?.checkin_status) === 0 ? 0 : 1;
  const typeKey = Number(record?.checkinType);
  const sourceKey = Number(record?.source);

  return {
    insurance_no: record?.insurance_no,
    code: record?.code,
    checkin_date: record?.checkin_date,
    facility_name: record?.facility_name,
    policy_no: record?.policy_no,
    statusLabel: STATUS_MAP[statusKey] || "Unknown",
    statusColor: STATUS_COLOR_MAP[statusKey] || "default",
    typeLabel: record?.checkin_type_name || CHECKIN_TYPE_MAP[typeKey] || null,
    typeColor: TYPE_COLOR_MAP[typeKey] || "geekblue",
    sourceLabel: CHECKIN_SOURCE_MAP[sourceKey] || null,
    sourceColor: SOURCE_COLOR_MAP[sourceKey] || "cyan",
    isSearch: false,
  };
}

/**
 * Compact linear encounter card for the encounters queue and search results.
 */
export function EncounterListingCard({ record, actions, onClick }) {
  const data = normalizeEncounter(record);

  return (
    <article
      className="listing-card listing-card--linear search-result-card"
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="listing-card__lead">
        <div className="listing-card__icon is-encounter">
          <MedicineBoxOutlined />
        </div>
        <div className="listing-card__identity">
          <h3 className="listing-card__title">{formatPatientName(record)}</h3>
          <p className="listing-card__meta">{data.insurance_no || "No insurance no"}</p>
        </div>
      </div>

      <div className="listing-card__facts">
        <Fact>{data.code}</Fact>
        <Fact>{formatSearchDate(data.checkin_date)}</Fact>
        <Fact>{data.facility_name}</Fact>
        {data.plan_name ? <Fact>{data.plan_name}</Fact> : null}
        {data.policy_no ? <Fact>Policy {data.policy_no}</Fact> : null}
        {data.isSearch && data.filedCount != null ? (
          <Fact>{data.filedCount} PA items</Fact>
        ) : null}
      </div>

      <div className="listing-card__aside" onClick={(e) => e.stopPropagation()}>
        <Tag color={data.statusColor} className="listing-card__tag">
          {data.statusLabel}
        </Tag>
        {!data.isSearch && data.typeLabel ? (
          <Tag color={data.typeColor} className="listing-card__tag">
            {data.typeLabel}
          </Tag>
        ) : null}
        {!data.isSearch && data.sourceLabel ? (
          <Tag color={data.sourceColor} className="listing-card__tag">
            {data.sourceLabel}
          </Tag>
        ) : null}
        {actions}
        {onClick ? <RightOutlined className="listing-card__chevron" aria-hidden /> : null}
      </div>
    </article>
  );
}
